import { FlowNode } from '../types/zapier';

/**
 * Represents a detected variable in a configuration value
 */
export interface DetectedVariable {
  /** Full variable expression, e.g., "{{trigger.email}}" */
  fullMatch: string;
  /** Variable path without braces, e.g., "trigger.email" */
  path: string;
  /** Source type: trigger, steps, webhook, etc. */
  sourceType: 'trigger' | 'steps' | 'webhook' | 'input' | 'numericStepId' | 'givesExpression' | 'unknown';
  /** Step index for steps references (0-indexed) */
  stepIndex?: number;
  /** Numeric Zapier step ID for GDPR exports (e.g., 109740420) */
  zapId?: string;
  /** Field path within the source, e.g., "email" or "response.Name" */
  fieldPath: string;
  /** Start position in the original string */
  startIndex: number;
  /** End position in the original string */
  endIndex: number;
}

/**
 * Information about the source node for a variable
 */
export interface VariableSource {
  /** The node that provides this variable's data */
  sourceNode: FlowNode | null;
  /** Human-readable description of the source */
  description: string;
  /** Path from trigger to source node */
  pathFromTrigger: FlowNode[];
  /** Whether the source is available (node exists) */
  isResolvable: boolean;
}

/**
 * Regex pattern to match Zapier variable syntax
 * Matches: {{trigger.field}}, {{steps.0.field}}, {{steps[1].field}}, {{webhook.data}}
 */
const VARIABLE_REGEX = /\{\{([^}]+)\}\}/g;

/**
 * Detects all variables in a string value
 */
export function detectVariables(value: string): DetectedVariable[] {
  const variables: DetectedVariable[] = [];
  const matches = value.matchAll(VARIABLE_REGEX);

  for (const match of matches) {
    const fullMatch = match[0];
    const path = match[1].trim();
    const startIndex = match.index!;
    const endIndex = startIndex + fullMatch.length;

    const parsed = parseVariablePath(path);

    variables.push({
      fullMatch,
      path,
      ...parsed,
      startIndex,
      endIndex,
    });
  }

  return variables;
}

/**
 * Parses a variable path to extract source type and field information
 * Examples:
 *   "trigger.email" → { sourceType: 'trigger', fieldPath: 'email' }
 *   "steps.1.name" → { sourceType: 'steps', stepIndex: 1, fieldPath: 'name' }
 *   "steps[0].data" → { sourceType: 'steps', stepIndex: 0, fieldPath: 'data' }
 *   "109740420__customer__email" → { sourceType: 'numericStepId', zapId: '109740420', fieldPath: 'customer.email' }
 *   "=gives['245099299']['customer']['email']" → { sourceType: 'givesExpression', zapId: '245099299', fieldPath: 'customer.email' }
 */
function parseVariablePath(path: string): {
  sourceType: DetectedVariable['sourceType'];
  stepIndex?: number;
  zapId?: string;
  fieldPath: string;
} {
  // Handle numeric step ID references: 109740420__customer__email
  // This is the format used in real Zapier GDPR exports
  const numericMatch = path.match(/^(\d+)__(.+)$/);
  if (numericMatch) {
    const zapId = numericMatch[1];
    const rawFieldPath = numericMatch[2];
    // Convert double underscores to dots for display, but preserve array notation
    const fieldPath = rawFieldPath.replace(/__/g, '.');

    return {
      sourceType: 'numericStepId',
      zapId,
      fieldPath,
    };
  }

  // Handle gives expression: =gives['245099299']['customer']['email']
  // This is a legacy format that appears in some exports
  const givesMatch = path.match(/^=gives\['(\d+)'\](?:\['([^']+)'\])+$/);
  if (givesMatch) {
    const zapId = givesMatch[1];
    // Extract all the field parts between quotes
    const fieldParts: string[] = [];
    const fieldMatches = path.matchAll(/\['([^']+)'\]/g);
    for (const match of fieldMatches) {
      if (match[1] !== zapId) {  // Skip the step ID part
        fieldParts.push(match[1]);
      }
    }
    const fieldPath = fieldParts.join('.');

    return {
      sourceType: 'givesExpression',
      zapId,
      fieldPath,
    };
  }

  // Handle steps references: steps.N.field or steps[N].field
  const stepsMatch = path.match(/^steps(?:\.|\[)(\d+)\]?\.(.+)$/);
  if (stepsMatch) {
    return {
      sourceType: 'steps',
      stepIndex: parseInt(stepsMatch[1], 10),
      fieldPath: stepsMatch[2],
    };
  }

  // Handle trigger references: trigger.field
  const triggerMatch = path.match(/^trigger\.(.+)$/);
  if (triggerMatch) {
    return {
      sourceType: 'trigger',
      fieldPath: triggerMatch[1],
    };
  }

  // Handle webhook references: webhook.field
  const webhookMatch = path.match(/^webhook\.(.+)$/);
  if (webhookMatch) {
    return {
      sourceType: 'webhook',
      fieldPath: webhookMatch[1],
    };
  }

  // Handle input/custom field references (e.g., lead.company_name)
  const dotIndex = path.indexOf('.');
  if (dotIndex > 0) {
    return {
      sourceType: 'input',
      fieldPath: path.substring(dotIndex + 1),
    };
  }

  // Unknown pattern
  return {
    sourceType: 'unknown',
    fieldPath: path,
  };
}

/**
 * Finds all variables recursively in a configuration object
 */
export function findVariablesInConfig(config: Record<string, any>): Map<string, DetectedVariable[]> {
  const variableMap = new Map<string, DetectedVariable[]>();

  function traverse(obj: any, keyPath: string[] = []) {
    if (typeof obj === 'string') {
      const variables = detectVariables(obj);
      if (variables.length > 0) {
        const key = keyPath.join('.');
        variableMap.set(key, variables);
      }
    } else if (Array.isArray(obj)) {
      obj.forEach((item, index) => {
        traverse(item, [...keyPath, `[${index}]`]);
      });
    } else if (obj && typeof obj === 'object') {
      Object.entries(obj).forEach(([key, value]) => {
        traverse(value, [...keyPath, key]);
      });
    }
  }

  traverse(config);
  return variableMap;
}

/**
 * Resolves a variable to its source node in the workflow
 */
export function resolveVariableSource(
  variable: DetectedVariable,
  _currentNode: FlowNode,
  allNodes: FlowNode[]
): VariableSource {
  // Find trigger node (first node or node with nodeType: 'trigger')
  const triggerNode = allNodes.find(n => n.data.nodeType === 'trigger') || allNodes[0];

  switch (variable.sourceType) {
    case 'numericStepId':
    case 'givesExpression':
      // Handle GDPR export format with numeric step IDs
      if (variable.zapId) {
        // Try to find the node by its zapId (stored in node.id or node.data.zapId)
        const sourceNode = allNodes.find(n =>
          n.id === variable.zapId ||
          n.data.zapId === variable.zapId ||
          String(n.id) === variable.zapId
        );

        if (sourceNode) {
          const pathFromTrigger = buildNodePath(triggerNode, sourceNode, allNodes);
          return {
            sourceNode,
            description: `${sourceNode.data.label || sourceNode.data.app} → ${variable.fieldPath}`,
            pathFromTrigger,
            isResolvable: true,
          };
        }

        // Node not found - might reference a step outside this workflow
        return {
          sourceNode: null,
          description: `Step ${variable.zapId} → ${variable.fieldPath} (node not found)`,
          pathFromTrigger: [],
          isResolvable: false,
        };
      }
      return {
        sourceNode: null,
        description: `Invalid step reference → ${variable.fieldPath}`,
        pathFromTrigger: [],
        isResolvable: false,
      };

    case 'trigger':
    case 'webhook':
      return {
        sourceNode: triggerNode || null,
        description: `Trigger: ${triggerNode?.data.label || 'Unknown'} → ${variable.fieldPath}`,
        pathFromTrigger: triggerNode ? [triggerNode] : [],
        isResolvable: !!triggerNode,
      };

    case 'steps':
      if (variable.stepIndex !== undefined) {
        // Steps are 0-indexed, and first step (index 0) is usually the trigger
        // So steps.0 = first action after trigger, steps.1 = second action, etc.
        const stepNode = allNodes[variable.stepIndex + 1]; // +1 to skip trigger

        if (stepNode) {
          // Build path from trigger to this step
          const pathFromTrigger = buildNodePath(triggerNode, stepNode, allNodes);

          return {
            sourceNode: stepNode,
            description: `Step ${variable.stepIndex + 1}: ${stepNode.data.label} → ${variable.fieldPath}`,
            pathFromTrigger,
            isResolvable: true,
          };
        }
      }
      return {
        sourceNode: null,
        description: `Step ${variable.stepIndex !== undefined ? variable.stepIndex + 1 : '?'} → ${variable.fieldPath} (not found)`,
        pathFromTrigger: [],
        isResolvable: false,
      };

    case 'input':
      // Input variables typically reference the trigger or webhook data
      return {
        sourceNode: triggerNode || null,
        description: `Input: ${variable.path} (from trigger)`,
        pathFromTrigger: triggerNode ? [triggerNode] : [],
        isResolvable: !!triggerNode,
      };

    default:
      return {
        sourceNode: null,
        description: `Unknown source: ${variable.path}`,
        pathFromTrigger: [],
        isResolvable: false,
      };
  }
}

/**
 * Builds the path of nodes from source to target
 * This helps visualize the data flow
 */
function buildNodePath(
  sourceNode: FlowNode | null,
  targetNode: FlowNode,
  _allNodes: FlowNode[]
): FlowNode[] {
  if (!sourceNode) return [targetNode];
  if (sourceNode.id === targetNode.id) return [sourceNode];

  // Simple path: just return source and target for now
  // In a more complex implementation, we could traverse the edge graph
  return [sourceNode, targetNode];
}

/**
 * Highlights variables in a string by wrapping them in a marker
 * Useful for rendering with styled spans
 */
export function highlightVariables(text: string): Array<{
  type: 'text' | 'variable';
  content: string;
  variable?: DetectedVariable;
}> {
  const variables = detectVariables(text);

  if (variables.length === 0) {
    return [{ type: 'text', content: text }];
  }

  const parts: Array<{
    type: 'text' | 'variable';
    content: string;
    variable?: DetectedVariable;
  }> = [];

  let lastIndex = 0;

  variables.forEach((variable) => {
    // Add text before variable
    if (variable.startIndex > lastIndex) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex, variable.startIndex),
      });
    }

    // Add variable
    parts.push({
      type: 'variable',
      content: variable.fullMatch,
      variable,
    });

    lastIndex = variable.endIndex;
  });

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.substring(lastIndex),
    });
  }

  return parts;
}

/**
 * Gets a summary of all variables used in a node's configuration
 */
export function getNodeVariableSummary(node: FlowNode, allNodes: FlowNode[]): {
  totalVariables: number;
  variablesBySource: Map<string, number>;
  unresolvedVariables: number;
} {
  const variableMap = findVariablesInConfig(node.data.config);
  const allVariables: DetectedVariable[] = [];

  variableMap.forEach(vars => allVariables.push(...vars));

  const variablesBySource = new Map<string, number>();
  let unresolvedVariables = 0;

  allVariables.forEach(variable => {
    const source = resolveVariableSource(variable, node, allNodes);

    if (!source.isResolvable) {
      unresolvedVariables++;
    }

    const sourceKey = source.sourceNode?.id || 'unresolved';
    variablesBySource.set(sourceKey, (variablesBySource.get(sourceKey) || 0) + 1);
  });

  return {
    totalVariables: allVariables.length,
    variablesBySource,
    unresolvedVariables,
  };
}

/**
 * Detects the data type of a configuration value containing a variable
 * Used for color-coding connection lines in the flow graph
 */
export function detectDataType(value: any): 'text' | 'number' | 'array' | 'object' | 'boolean' | 'unknown' {
  if (value === null || value === undefined) {
    return 'unknown';
  }

  if (Array.isArray(value)) {
    return 'array';
  }

  const valueType = typeof value;

  switch (valueType) {
    case 'string': {
      // Check if it's a number string
      if (/^\d+(\.\d+)?$/.test(value)) {
        return 'number';
      }
      // Check if it's a boolean string
      if (value === 'true' || value === 'false') {
        return 'boolean';
      }
      // Check if it looks like JSON array/object
      const trimmed = value.trim();
      if ((trimmed.startsWith('[') && trimmed.endsWith(']')) ||
          (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
        try {
          const parsed = JSON.parse(trimmed);
          return Array.isArray(parsed) ? 'array' : typeof parsed === 'object' ? 'object' : 'text';
        } catch {
          return 'text';
        }
      }
      return 'text';
    }
    case 'number':
      return 'number';
    case 'boolean':
      return 'boolean';
    case 'object':
      return 'object';
    default:
      return 'unknown';
  }
}
